import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { TicketAndPassesForm } from "Bands/components";
import { useBandContext } from "Bands/contexts";
import {
  BAND_ACTIONS,
  IGuestlistTicketAndPass,
  IGuestlistTicketAndPassParams,
} from "Bands/types";
import { copy } from "shared/assets";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { createEventTicketAndPass, dataMetaSelector } from "Bands/state";
import { API_ENDPOINTS } from "shared/constants";
import { useClearActionMetaOnUnMount } from "Venues/utils";
import { addTemporaryToast } from "shared/components/Toast";

const ticketAndPassCopy = copy.guestlistBlock.ticketAndPasses.create;

interface CreateTicketAndPassFormProps {
  blockId: string;
  addedTicketAndPasses: IGuestlistTicketAndPass[];
}

export const CreateTicketAndPassForm: React.FC<CreateTicketAndPassFormProps> =
  ({ blockId, addedTicketAndPasses }) => {
    const dispatch = useDispatch();
    const { ticketAndPassTypes } = useBandContext();
    const [resetForm, setResetForm] = useState(false);

    const { api: apiMeta } = useSelector(
      dataMetaSelector(BAND_ACTIONS.createEventTicketAndPass)
    );

    useClearActionMetaOnUnMount(BAND_ACTIONS.createEventTicketAndPass);

    useEffect(() => {
      if (apiMeta?.status === IAPIState.succeeded) {
        setResetForm(true);

        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: ticketAndPassCopy.messages.success,
          })
        );
      }
    }, [apiMeta?.status, dispatch]);

    const handleAdd = ({
      ticketsTotal,
      isUnlimited,
      ...rest
    }: IGuestlistTicketAndPassParams) => {
      setResetForm(false);

      const cleanTicketAndPass: IGuestlistTicketAndPassParams = {
        ...rest,
        ticketsTotal: isUnlimited ? null : ticketsTotal,
        isUnlimited: !!isUnlimited,
        guestListBlock: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(blockId),
      };

      dispatch(createEventTicketAndPass(cleanTicketAndPass));
    };

    const defaultValues: IGuestlistTicketAndPassParams = useMemo(
      () => ({
        ticketsTotal: 1,
      }),
      []
    );

    const buttons: IFormButtons = useMemo(
      () => ({
        primary: {
          children: ticketAndPassCopy.buttons.create,
          size: "m",
          "data-testid": "ticket_pass_submit",
        },
      }),
      []
    );

    const filteredTicketAndPassTypes = useMemo(
      () =>
        ticketAndPassTypes.filter(
          ({ id }) =>
            !addedTicketAndPasses.find(
              ({ ticketAndPassType }) => ticketAndPassType.id === id
            )
        ),
      [ticketAndPassTypes, addedTicketAndPasses]
    );

    return (
      <TicketAndPassesForm
        resetForm={resetForm}
        defaultValues={defaultValues}
        ticketAndPassTypes={filteredTicketAndPassTypes}
        buttons={buttons}
        handleSubmit={handleAdd}
        apiStatus={apiMeta?.status}
        apiError={apiMeta?.error}
        error={!!apiMeta?.error}
      />
    );
  };
